import { useActiveConversation } from '../components/chat/ActiveConversationContext';
import { useChatOverlayOpen } from './use-chat-overlay';
import { useChatsLayout } from './use-chats-layout';
import { useNarrow } from './use-narrow';
import { useSitePaneRequest, type SitePaneRequest } from './site-edit-store';

/**
 * The chat can host the site preview pane beside it: the same wide, un-stacked test the chat's file pane uses
 * (`lib/chat-file-pane.ts`). Where it cannot, the chat is a full-screen sheet and a Symphony site's Edit behaves as on
 * a phone: the chat alone, plus "Review site".
 */
export function useSitePaneFits(): boolean {
  const narrow = useNarrow();
  const { stacked } = useChatsLayout();
  return !narrow && !stacked;
}

/**
 * The site preview pane to draw beside the chat, or null. Decided ONCE here because FloatingChat (which splits) and
 * AppFrame (whose `--chat-w` must stop reserving a dock column while the split chat covers the content) must agree.
 *
 * It shows while the chat is open, the layout fits, and the open conversation is the one it was opened for (or, while
 * that chat is still being created, a new chat). Switching to another conversation hides it without dropping it, so
 * coming back shows it again with its review intact; its own ✕ closes it.
 */
export function useSitePane(): SitePaneRequest | null {
  const request = useSitePaneRequest();
  const chatOpen = useChatOverlayOpen();
  const fits = useSitePaneFits();
  const { activeConversationId } = useActiveConversation();
  if (!request || !chatOpen || !fits) return null;
  if (request.conversationId !== null && request.conversationId !== activeConversationId) return null;
  if (request.conversationId === null && activeConversationId !== null) return null;
  return request;
}
